import Spacing from '../Spacing';
import SectionHeadingStyle3 from '../SectionHeading/SectionHeadingStyle3';
import { pageTitle } from '../../helpers/PageTitle';

export default function PrivacyPolicyPage() {
  pageTitle('Privacy Policy — Ranads');

  return (
    <>
      <Spacing lg='70' md='70' />
      <Spacing lg='140' md='80' />

      <SectionHeadingStyle3
        title='Your data, your control, <br>our commitment'
        subTitle='Privacy Policy'
        variant='text-center'
        shape='shape_1'
      />

      <Spacing lg='75' md='60' />

      <div className='container'>
        <div className='row'>
          <div className='col-lg-10 offset-lg-1'>
            <Spacing lg='40' md='30' />

            {/* Introduction */}
            <section className='cs_privacy_section'>
              <h2 className='cs_fs_40 cs_fs_lg_30 cs_semibold mb-3'>
                Introduction
              </h2>
              <p className='cs_fs_18 cs_lh_base'>
                When you use our services, you trust us with your information.
                At RANADS S.A.R.L. ("RANADS," "we," "us," or "our"), we
                understand this is a significant responsibility, and we work
                hard to protect your data while putting you in control.
              </p>
              <p className='cs_fs_18 cs_lh_base mb-0'>
                This Privacy Policy explains what information we collect, why we
                collect it, and how you can manage or request deletion of your
                information.
              </p>
            </section>

            <Spacing lg='50' md='40' />

            {/* Who We Are */}
            <section className='cs_privacy_section'>
              <h2 className='cs_fs_40 cs_fs_lg_30 cs_semibold mb-3'>
                Who We Are
              </h2>
              <p className='cs_fs_18 cs_lh_base'>
                RANADS S.A.R.L. is a digital innovation and media company based
                in Charles Malek Avenue, Achrafieh, Lebanon, specializing in
                software development (React & NestJS), ERP & CRM systems,
                digital marketing, and brand communication.
              </p>
              <p className='cs_fs_18 cs_lh_base mb-0'>
                We build and operate technology platforms, websites, and
                marketing systems that connect brands with their audiences and
                automate business workflows.
              </p>
            </section>

            <Spacing lg='50' md='40' />

            {/* Information We Collect */}
            <section className='cs_privacy_section'>
              <h2 className='cs_fs_40 cs_fs_lg_30 cs_semibold mb-4'>
                Information We Collect and Why
              </h2>
              <p className='cs_fs_18 cs_lh_base mb-4'>
                RANADS collects and processes personal data for the following
                purposes:
              </p>

              {/* Subsection 1 */}
              <div className='cs_privacy_subsection mb-5'>
                <h3 className='cs_fs_28 cs_fs_lg_24 cs_medium mb-3'>
                  1. Providing and Managing Our Services
                </h3>
                <p className='cs_fs_18 cs_lh_base mb-3'>
                  We collect information to deliver, improve, and maintain our
                  services. This includes:
                </p>
                <ul className='cs_list cs_style_1 cs_fs_18 cs_lh_base cs_mb_30'>
                  <li>
                    <strong>Name, email, and phone number</strong> – to identify
                    you and communicate about your account or project.
                  </li>
                  <li>
                    <strong>Business details</strong> – for billing, onboarding,
                    and support.
                  </li>
                  <li>
                    <strong>Technical usage data</strong> – such as browser, IP
                    address, and device type, to enhance website performance and
                    security.
                  </li>
                </ul>
                <p className='cs_fs_18 cs_lh_base mb-0'>
                  We store all data securely on our servers and hosting
                  providers located in the EU or other trusted data regions.
                </p>
              </div>

              {/* Subsection 2 */}
              <div className='cs_privacy_subsection mb-5'>
                <h3 className='cs_fs_28 cs_fs_lg_24 cs_medium mb-3'>
                  2. Communication and Marketing
                </h3>
                <p className='cs_fs_18 cs_lh_base mb-3'>
                  We may use your information to:
                </p>
                <ul className='cs_list cs_style_1 cs_fs_18 cs_lh_base cs_mb_30'>
                  <li>Respond to inquiries or support requests.</li>
                  <li>
                    Send marketing materials, updates, and newsletters (only
                    with your consent).
                  </li>
                  <li>
                    Provide insights about new features, offers, or service
                    updates.
                  </li>
                </ul>
                <p className='cs_fs_18 cs_lh_base mb-0'>
                  You can opt out of marketing communications at any time by
                  clicking "Unsubscribe" in emails or contacting us at{' '}
                  <a href='mailto:info@ranads.com' className='cs_text_accent'>
                    info@ranads.com
                  </a>
                  .
                </p>
              </div>

              {/* Subsection 3 */}
              <div className='cs_privacy_subsection mb-5'>
                <h3 className='cs_fs_28 cs_fs_lg_24 cs_medium mb-3'>
                  3. Internal Business Operations
                </h3>
                <p className='cs_fs_18 cs_lh_base mb-3'>
                  We process data for internal business functions such as:
                </p>
                <ul className='cs_list cs_style_1 cs_fs_18 cs_lh_base'>
                  <li>Human resources and recruitment.</li>
                  <li>Vendor management.</li>
                  <li>Analytics, reporting, and product improvement.</li>
                  <li>Fraud prevention and system security.</li>
                </ul>
              </div>

              {/* Subsection 4 */}
              <div className='cs_privacy_subsection'>
                <h3 className='cs_fs_28 cs_fs_lg_24 cs_medium mb-3'>
                  4. Payments
                </h3>
                <p className='cs_fs_18 cs_lh_base mb-3'>
                  For paid projects or services, we collect billing and payment
                  details to process transactions securely through trusted
                  third-party payment providers.
                </p>
                <p className='cs_fs_18 cs_lh_base mb-0'>
                  <strong>
                    RANADS does not store full credit card information on its
                    servers.
                  </strong>
                </p>
              </div>
            </section>

            <Spacing lg='50' md='40' />

            {/* Third-Party Services */}
            <section className='cs_privacy_section'>
              <h2 className='cs_fs_40 cs_fs_lg_30 cs_semibold mb-3'>
                Third-Party Services
              </h2>
              <p className='cs_fs_18 cs_lh_base mb-3'>
                RANADS may integrate or rely on third-party platforms for
                analytics, authentication, or communication, including:
              </p>
              <ul className='cs_list cs_style_1 cs_fs_18 cs_lh_base cs_mb_30'>
                <li>
                  <strong>Google Analytics</strong> – for traffic and
                  performance insights.
                </li>
                <li>
                  <strong>Meta Platforms (Facebook, Instagram)</strong> – for
                  marketing and ad performance.
                </li>
                <li>
                  <strong>LinkedIn & Google Ads</strong> – for business
                  communications and advertising.
                </li>
                <li>
                  <strong>Stripe or similar gateways</strong> – for payment
                  processing.
                </li>
              </ul>
              <p className='cs_fs_18 cs_lh_base mb-0'>
                Each service is governed by its own privacy policy. We encourage
                users to review those separately.
              </p>
            </section>

            <Spacing lg='50' md='40' />

            {/* Cookies */}
            <section className='cs_privacy_section'>
              <h2 className='cs_fs_40 cs_fs_lg_30 cs_semibold mb-3'>Cookies</h2>
              <p className='cs_fs_18 cs_lh_base mb-3'>
                RANADS uses cookies and similar tracking technologies to:
              </p>
              <ul className='cs_list cs_style_1 cs_fs_18 cs_lh_base cs_mb_30'>
                <li>Improve site functionality and load speed.</li>
                <li>Analyze traffic and user behavior.</li>
                <li>Remember user preferences.</li>
              </ul>
              <p className='cs_fs_18 cs_lh_base mb-0'>
                You can manage or disable cookies from your browser settings at
                any time. Please note that some features may not function
                properly if cookies are disabled.
              </p>
            </section>

            <Spacing lg='50' md='40' />

            {/* Data Security */}
            <section className='cs_privacy_section'>
              <h2 className='cs_fs_40 cs_fs_lg_30 cs_semibold mb-3'>
                Data Security
              </h2>
              <p className='cs_fs_18 cs_lh_base mb-3'>
                We take data protection seriously and implement security
                measures such as:
              </p>
              <ul className='cs_list cs_style_1 cs_fs_18 cs_lh_base cs_mb_30'>
                <li>SSL encryption on all data transfers.</li>
                <li>Access control and authentication systems.</li>
                <li>Encrypted storage of sensitive data.</li>
                <li>Regular backups and server monitoring.</li>
              </ul>
              <p className='cs_fs_18 cs_lh_base mb-0'>
                While we strive to ensure data security, no system is 100%
                immune from breaches. In the unlikely event of a data incident,
                we will notify affected parties in accordance with applicable
                laws.
              </p>
            </section>

            <Spacing lg='50' md='40' />

            {/* Data Retention */}
            <section className='cs_privacy_section'>
              <h2 className='cs_fs_40 cs_fs_lg_30 cs_semibold mb-3'>
                Data Retention
              </h2>
              <p className='cs_fs_18 cs_lh_base mb-3'>
                We retain your personal information only for as long as
                necessary to:
              </p>
              <ul className='cs_list cs_style_1 cs_fs_18 cs_lh_base cs_mb_30'>
                <li>Provide services.</li>
                <li>Comply with legal obligations.</li>
                <li>Resolve disputes.</li>
              </ul>
              <p className='cs_fs_18 cs_lh_base mb-0'>
                If you wish to have your data deleted, email{' '}
                <a href='mailto:info@ranads.com' className='cs_text_accent'>
                  info@ranads.com
                </a>
                , and we will permanently remove it unless required to retain it
                for legal or contractual reasons.
              </p>
            </section>

            <Spacing lg='50' md='40' />

            {/* Your Rights */}
            <section className='cs_privacy_section'>
              <h2 className='cs_fs_40 cs_fs_lg_30 cs_semibold mb-3'>
                Your Rights
              </h2>
              <p className='cs_fs_18 cs_lh_base mb-3'>
                Under applicable data protection laws, you have the right to:
              </p>
              <ul className='cs_list cs_style_1 cs_fs_18 cs_lh_base cs_mb_30'>
                <li>Access your data.</li>
                <li>Request correction of inaccurate information.</li>
                <li>Request deletion of your personal data.</li>
                <li>Withdraw consent to data processing.</li>
                <li>Object to data processing for marketing purposes.</li>
                <li>Request data portability (receive a copy of your data).</li>
              </ul>
              <p className='cs_fs_18 cs_lh_base mb-0'>
                To exercise any of these rights, contact us at{' '}
                <a href='mailto:info@ranads.com' className='cs_text_accent'>
                  info@ranads.com
                </a>
                .
              </p>
            </section>

            <Spacing lg='50' md='40' />

            {/* International Data Transfers */}
            <section className='cs_privacy_section'>
              <h2 className='cs_fs_40 cs_fs_lg_30 cs_semibold mb-3'>
                International Data Transfers
              </h2>
              <p className='cs_fs_18 cs_lh_base mb-3'>
                Some of our systems and cloud services may process data outside
                Lebanon (for example, within the EU or other jurisdictions with
                adequate data protection).
              </p>
              <p className='cs_fs_18 cs_lh_base mb-0'>
                We ensure that any international transfer complies with data
                protection laws and safeguards your personal information.
              </p>
            </section>

            <Spacing lg='50' md='40' />

            {/* Children's Privacy */}
            <section className='cs_privacy_section'>
              <h2 className='cs_fs_40 cs_fs_lg_30 cs_semibold mb-3'>
                Children's Privacy
              </h2>
              <p className='cs_fs_18 cs_lh_base mb-3'>
                Our services are not directed at children under 16.
              </p>
              <p className='cs_fs_18 cs_lh_base mb-0'>
                If you believe a child has provided us with personal data,
                please contact us, and we will delete it immediately.
              </p>
            </section>

            <Spacing lg='50' md='40' />

            {/* Changes to This Policy */}
            <section className='cs_privacy_section'>
              <h2 className='cs_fs_40 cs_fs_lg_30 cs_semibold mb-3'>
                Changes to This Policy
              </h2>
              <p className='cs_fs_18 cs_lh_base mb-3'>
                We may update this Privacy Policy from time to time to reflect
                changes in technology, legal requirements, or our practices.
              </p>
              <p className='cs_fs_18 cs_lh_base mb-3'>
                We encourage you to review this page periodically. The updated
                date will always be shown at the top of this document.
              </p>
              <p className='cs_fs_18 cs_lh_base mb-0'>
                By continuing to use our services after any updates, you agree
                to the revised policy.
              </p>
            </section>

            <Spacing lg='50' md='40' />

            {/* Contact Us */}
            <section className='cs_privacy_section cs_gray_bg_2 cs_rounded_20 p-4 p-lg-5'>
              <h2 className='cs_fs_40 cs_fs_lg_30 cs_semibold mb-3'>
                Contact Us
              </h2>
              <p className='cs_fs_18 cs_lh_base mb-4'>
                If you have any questions, concerns, or requests about this
                Privacy Policy or your personal data, please contact:
              </p>

              <div className='cs_contact_info'>
                <p className='cs_fs_18 cs_medium mb-2'>
                  <strong>RANADS S.A.R.L.</strong>
                </p>
                <p className='cs_fs_18 cs_lh_base mb-2'>
                  Charles Malek Avenue, Achrafieh, Beirut, Lebanon
                </p>
                <p className='cs_fs_18 cs_lh_base mb-2'>
                  📞{' '}
                  <a href='tel:+96170080040' className='cs_text_accent'>
                    +961 70 080 040
                  </a>
                </p>
                <p className='cs_fs_18 cs_lh_base mb-4'>
                  ✉️{' '}
                  <a href='mailto:info@ranads.com' className='cs_text_accent'>
                    info@ranads.com
                  </a>
                </p>

                <p className='cs_fs_18 cs_medium mb-2'>
                  <strong>Data Protection Contact:</strong>
                </p>
                <p className='cs_fs_18 cs_lh_base mb-2'>
                  Compliance Department – RANADS
                </p>
                <p className='cs_fs_18 cs_lh_base mb-0'>
                  Email:{' '}
                  <a
                    href='mailto:privacy@ranads.com'
                    className='cs_text_accent'
                  >
                    privacy@ranads.com
                  </a>
                </p>
              </div>
            </section>

            <Spacing lg='60' md='50' />
          </div>
        </div>
      </div>

      <Spacing lg='150' md='80' />
    </>
  );
}
